import { useTranslation } from 'react-i18next';
import { EditableDraftTable, type DraftColumnDef } from './EditableDraftTable';
import { type DraftRowShape } from '@/lib/draft-rows/draftRow';

interface ExistingEntitiesTableProps<TData> {
  /** Existing entities mapped into the step's draft-row data shape */
  rows: DraftRowShape<TData>[];
  /** Usually the step's own column defs, so both tables look identical */
  columns: DraftColumnDef<TData>[];
  /** Optional note under the title (e.g. "existing members survive the import") */
  description?: string;
  /** Extra classes on a data row — marks rows with special provenance, e.g. Stelle-derived Funktionen. */
  rowClassName?: (row: DraftRowShape<TData>) => string | undefined;
}

/**
 * What already exists in the org, rendered as the same table the uploads
 * use — read-only, because editing existing entities stays in the
 * Organisationsverwaltung.
 */
export function ExistingEntitiesTable<TData>({
  rows,
  columns,
  description,
  rowClassName
}: ExistingEntitiesTableProps<TData>) {
  const { t } = useTranslation();

  if (rows.length === 0) return null;

  return (
    <div>
      <h3 className="text-sm font-medium text-foreground mb-2">
        {t('initialSetup.existingEntities.title', { count: rows.length })}
      </h3>
      {description && <p className="mb-2 text-xs text-muted-foreground">{description}</p>}
      <EditableDraftTable rows={rows} columns={columns} readOnly rowClassName={rowClassName} />
    </div>
  );
}

export function toExistingRows<TData>(items: TData[]): DraftRowShape<TData>[] {
  return items.map((data, index) => ({ data, isValid: true, rowNumber: index + 1 }));
}
